import React, { FunctionComponent, SyntheticEvent, ReactNode, useState } from 'react'
import { Button, Radio } from '@afterpaytouch/core'
import { useTranslation } from '../../../utils/convergence'
import { AssetId } from '../../../model'
import clsx from 'clsx'

import styles from './styles.module.css'
import { getCta } from '../helper'
import { useGetLocaleWithFallback, usePreferredMarketingNotification } from '../../../utils/post-checkout'
import { AssetChildComponentSpacing, MarginSize } from './types'
import { getSpacingClassName } from './helper'

enum ActionType {
  Positive,
  Negative,
}

interface Props extends AssetChildComponentSpacing {
  id: AssetId
  testNameSpace?: string
  onPrimaryActionClick?: (e: SyntheticEvent<HTMLInputElement>) => void
  onNegativeActionClick?: (e: SyntheticEvent<HTMLInputElement>) => void
  disabled?: boolean
  show?: boolean
  buttonType?: 'radio' | 'default'
}

export const CallToAction: FunctionComponent<Props> = ({
  onPrimaryActionClick = () => {},
  onNegativeActionClick = () => {},
  id,
  testNameSpace = 'postCheckout',
  disabled = false,
  show = false,
  buttonType = 'default',
  margin,
  marginTop = MarginSize.x2,
  marginBottom,
}) => {
  const { t } = useTranslation()
  const [selectedValue, setSelectedValue] = useState('')
  const notificationMethod = usePreferredMarketingNotification()
  const localeWithFallback = useGetLocaleWithFallback()

  const ctaStyle = clsx(styles.callToAction, getSpacingClassName({ margin, marginTop, marginBottom }))
  const translationOption = {
    returnObjects: true,
    defaultValue: [],
    context: notificationMethod,
  }

  const buttonLabelMap = {
    [ActionType.Positive]: getCta(t(localeWithFallback('positiveCta'), translationOption)),
    [ActionType.Negative]: getCta(t(localeWithFallback('negativeCta'), translationOption)),
  }
  const positiveButtonCount = buttonLabelMap[ActionType.Positive].length
  const negativeButtonCount = buttonLabelMap[ActionType.Negative].length

  const handleClick = (e: SyntheticEvent<HTMLInputElement>, type: ActionType): void => {
    setSelectedValue(e.currentTarget.value)
    switch (type) {
      case ActionType.Positive:
        onPrimaryActionClick(e)
        break
      case ActionType.Negative:
        onNegativeActionClick(e)
        break
    }
  }

  const renderButton = (i: number, type: ActionType, offset = 0): ReactNode => {
    const buttonValue = `${id}-${i}`
    const buttonStyles = clsx(styles[`${buttonType}Button`], selectedValue === buttonValue && styles[`${buttonType}ButtonSelected`])

    const label = buttonLabelMap[type][i]

    const defaultProps = {
      value: `${id}-${i + offset}`,
      testNameSpace: `${testNameSpace}-${i + offset}`,
      onClick: (e: SyntheticEvent<HTMLInputElement>) => handleClick(e, type),
      name: id,
      id: `${id}-${i + offset}`,
      'data-label': label,
      disabled,
    }

    return (
      <div className={buttonStyles} key={`postcheckout-${id}-${i + offset}`}>
        {buttonType === 'radio' && <Radio.Button {...defaultProps} icon='Select' labelWidth='Full' label={label} />}
        {buttonType === 'default' && (
          <Button.Ghost {...defaultProps} padding='Fluid' size='Regular' role='button'>
            {label}
          </Button.Ghost>
        )}
      </div>
    )
  }

  if (!show || (positiveButtonCount === 0 && negativeButtonCount === 0)) {
    return null
  }

  return (
    <div className={ctaStyle}>
      {Array.from({ length: positiveButtonCount }, (_, i) => renderButton(i, ActionType.Positive))}
      {Array.from({ length: negativeButtonCount }, (_, i) => renderButton(i, ActionType.Negative, positiveButtonCount))}
    </div>
  )
}
